import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { ChartData, ChartOptions } from 'chart.js';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { fadeInOut } from '../../services/animations';
import { ConfigurationService } from '../../services/configuration.service';
import { AuthService } from '../../services/auth.service';
import { SfDataService } from '../../services/sf-data.service';
import { HubReportStatusService } from '../../services/hub-report-status.service';
import { environment } from '../../../environments/environment';

interface WidgetIndex { element: string, index: number }

@Component({
    selector: 'app-home',
    templateUrl: './home.component.html',
    styleUrls: ['./home.component.scss'],
    animations: [fadeInOut],
    standalone: false
})
export class HomeComponent implements AfterViewInit, OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  // Feature 9 — weekly report reminder on the dashboard.
  reportDue = false;
  reportWeekEnding = '';
  // True once the report is due and we're on day 4 (Thursday) or later — escalates to a red/flashing banner.
  reportUrgent = false;

  dragStartDelay = 200;
  readonly DBKeyWidgetsOrder = 'home-component.widgets_order';
  sFirstName = '';
  sLastName = '';
  sEmail = '';

  stats: any = null;
  statsLoading = true;

  allTrendData: { month: string; count: number }[] = [];
  trendRange = 'all';
  trendChartData: ChartData<'line', number[], string> = { labels: [], datasets: [] };
  trendChartOptions: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { mode: 'index', intersect: false }
    },
    scales: {
      y: { beginAtZero: true, ticks: { stepSize: 1, precision: 0 } },
      x: { grid: { display: false } }
    }
  };

  @ViewChild('widgetsContainer', { read: ElementRef })
  widgetsContainer: ElementRef<HTMLDivElement>

  constructor(private authService: AuthService, public configurations: ConfigurationService, private sfDataService: SfDataService, private hubReportStatus: HubReportStatusService, private router: Router) {

  }

  ngOnInit(): void {
    this.hubReportStatus.context.pipe(takeUntil(this.destroy$)).subscribe(ctx => {
      this.reportDue = !!ctx?.missingTemplate;
      this.reportWeekEnding = this.formatWeek(ctx?.currentWeekEnding || '');
    });
    this.hubReportStatus.reportUrgent.pipe(takeUntil(this.destroy$)).subscribe(urgent => this.reportUrgent = urgent);
    this.hubReportStatus.refresh();

    this.sfDataService.getDashboardStats().subscribe({
      next: (data: any) => {
        this.stats = data;
        this.statsLoading = false;
        if (data.trendData?.length) {
          this.allTrendData = data.trendData;
          this.setTrendRange(this.trendRange);
        }
      },
      error: () => { this.statsLoading = false; }
    });
  }

  setTrendRange(range: string) {
    this.trendRange = range;
    const counts: Record<string, number> = { '3m': 3, '6m': 6, '1y': 12, '2y': 24 };
    const slice = range !== 'all' ? this.allTrendData.slice(-(counts[range] ?? this.allTrendData.length)) : this.allTrendData;
    this.trendChartData = {
      labels: slice.map(d => d.month),
      datasets: [{
        data: slice.map(d => d.count),
        label: 'ICD Submissions',
        borderColor: '#007bff',
        backgroundColor: 'rgba(0,123,255,0.08)',
        pointBackgroundColor: '#007bff',
        pointRadius: 4,
        fill: true,
        tension: 0.3
      }]
    };
  }


  initializeLiveAgent() {
    this.initEmbeddedMessaging();
  }

  loadScripts() {
    const cfg = environment.salesforceChat;
    if (!cfg || !cfg.enabled) {
      return;
    }
    const script1 = document.createElement('script');
    script1.type = 'text/javascript';
    script1.src = `${cfg.baseUrl}/assets/js/bootstrap.min.js`;
    script1.onload = () => this.initEmbeddedMessaging();
    document.body.appendChild(script1);
  }

  initEmbeddedMessaging() {
    const cfg = environment.salesforceChat;
    if (!cfg || !cfg.enabled) {
      return;
    }
    try {
      (window as any).embeddedservice_bootstrap.settings.language = cfg.language;
      (window as any).embeddedservice_bootstrap.init(
        cfg.orgId,
        cfg.eswConfigName,
        cfg.baseUrl,
        {
          scrt2URL: cfg.scrt2URL  // browser calls this directly — origin must be in SF CORS allowlist
        }
      );
    } catch (err) {
      console.error('Error loading Embedded Messaging: ', err);
    }

    window.addEventListener('onEmbeddedMessagingReady', () => {
      ////console.log('onEmbeddedMessagingReady event triggered');
      (window as any).embeddedservice_bootstrap.prechatAPI.setVisiblePrechatFields({
        "_firstName": {
          "value": this.authService.currentUser ? this.authService.currentUser.fullName.split(' ')[0] : '',
          "isEditableByEndUser": true
        },
        "_lastName": {
          "value": this.authService.currentUser ? this.authService.currentUser.fullName.split(' ')[1] : '', 
          "isEditableByEndUser": true
        },
        "_email": {
          "value": this.authService.currentUser ? this.authService.currentUser.email : '',
          "isEditableByEndUser": true
        }
      });
    });
  }

  ngAfterViewInit(): void {
    this.restoreWidgetsOrder();
    // loadScripts() is a no-op unless environment.salesforceChat.enabled is true,
    // so this works for both dev (sandbox) and prod once the org values are configured.
    this.loadScripts();
  }

  restoreWidgetsOrder() {
    const widgetIndexes = this.loadWidgetIndexes();

    if (widgetIndexes == null || widgetIndexes.length == 0)
      return;

    const parentEle = this.widgetsContainer.nativeElement;

    for (var i = 0; i < parentEle.children.length; i++) {
      const widget = parentEle.children[i];
      const index = widgetIndexes.find(w => w.element == widget.id)?.index;

      if (index != null)
        this.insertChildAtIndex(parentEle, widget, index);
    }
  }

  insertChildAtIndex(parent: HTMLDivElement, child: Element, index: number) {
    if (!index)
      index = 0

    if (index >= parent.children.length) {
      parent.appendChild(child)
    } else {
      parent.insertBefore(child, parent.children[index])
    }
  }

  drop(event: CdkDragDrop<HTMLDivElement>) {
    const el = event.item.element.nativeElement;
    const parentEle = event.container.element.nativeElement;
    const anchorEle = parentEle.children[event.currentIndex];

    const widgetIndexes = new Array<WidgetIndex>(parentEle.children.length);

    for (var i = 0; i < parentEle.children.length; i++) {
      widgetIndexes[i] = { element: parentEle.children[i].id, index: i };
    }

    moveItemInArray(widgetIndexes, event.previousIndex, event.currentIndex)

    for (var i = 0; i < widgetIndexes.length; i++) {
      widgetIndexes[i].index = i;
    }

    if (event.currentIndex < event.previousIndex)
      parentEle.insertBefore(el, anchorEle);
    else
      parentEle.insertBefore(el, anchorEle.nextElementSibling);

    this.saveWidgetIndexes(widgetIndexes);
  }


  saveWidgetIndexes(indexes: WidgetIndex[]) {
    this.configurations
      .saveConfiguration(indexes, `${this.DBKeyWidgetsOrder}:${this.authService.currentUser?.id}`);
  }

  loadWidgetIndexes() {
    return this.configurations
      .getConfiguration<WidgetIndex[]>(`${this.DBKeyWidgetsOrder}:${this.authService.currentUser?.id}`);
  }

  formatWeek(iso: string): string {
    if (!iso) { return ''; }
    const parts = iso.split('-');
    return parts.length === 3 ? `${parseInt(parts[1], 10)}/${parseInt(parts[2], 10)}/${parts[0]}` : iso;
  }

  navigateToDistributorSearch(accountName: string): void {
    this.router.navigate(['/icdreport'], {
      queryParams: {
        searchTerm: accountName
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

}
